import { ArrowDown, MapPin, Compass, Sparkles } from 'lucide-react';
import { cleanImageUrl, getImageStyleFromUrl } from '../types';

interface HeroProps {
  onExplore: () => void;
  backgroundImage?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
}

const DEFAULT_BG = "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&q=80&w=1800";
const DEFAULT_TITLE = "Where Nature Whispers & Memories Take Root.";
const DEFAULT_SUBTITLE = "A cherished private sanctuary nestled in the serene valleys of Sirudavoor, Tamil Nadu. A haven where generations gather, harvests are celebrated, and time moves at nature's gentle pace.";
const DEFAULT_BUTTON = "Explore Our Sanctuary";

export default function Hero({ onExplore, backgroundImage, title, subtitle, buttonText }: HeroProps) {
  const bg = backgroundImage || DEFAULT_BG;
  const heroTitle = title || DEFAULT_TITLE;
  const heroSubtitle = subtitle || DEFAULT_SUBTITLE;
  const heroButton = buttonText || DEFAULT_BUTTON;

  const bgStyle = getImageStyleFromUrl(bg);
  const cleanBg = cleanImageUrl(bg);

  return (
    <section id="hero" className="relative w-full overflow-hidden flex items-center min-h-[92vh] sm:min-h-[90vh]">
      {/* Background Photography with subtle zoom effect */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 ease-out scale-105"
        style={{
          backgroundImage: `url('${cleanBg}')`,
          ...bgStyle
        }}
      />

      {/* Cinematic Nature Vignette Overlay */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-emerald-950/90 via-emerald-950/60 to-black/30"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-transparent to-black/40"
      />

      {/* Hero Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-20 md:py-28 animate-fadeIn">
        <div className="max-w-2xl text-white flex flex-col gap-6 md:gap-8">

          {/* Floating Location & Estate Sanctuary Badge */}
          <div className="inline-flex items-center gap-2 self-start bg-black/40 backdrop-blur-md border border-white/20 px-4 py-1.5 rounded-full shadow-lg">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-sans text-xs font-medium text-emerald-200 uppercase tracking-wider">
              Mithran Farm House
            </span>
            <span className="text-white/30 text-xs">•</span>
            <a
              href="https://maps.app.goo.gl/VCtYoxuA4cLNtSZHA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-300 hover:text-white transition-colors"
            >
              <MapPin size={12} />
              Sirudavoor, Chengalpattu
            </a>
          </div>

          {/* Monumental Editorial Headline */}
          <h1 className="font-serif text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.08] tracking-tight drop-shadow-md">
            {heroTitle}
          </h1>

          {/* Warm Sanctuary Narrative Lead */}
          <p className="font-sans text-stone-200 text-base sm:text-lg md:text-xl font-normal leading-relaxed max-w-xl drop-shadow-sm opacity-95">
            {heroSubtitle}
          </p>

          {/* Action CTAs */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
            <button
              onClick={onExplore}
              className="btn-estate-gold text-base font-semibold px-8 py-3.5 shadow-xl hover:shadow-2xl gap-2.5"
            >
              <span>{heroButton}</span>
              <ArrowDown size={16} className="animate-bounce" />
            </button>

            <a
              href="https://maps.app.goo.gl/VCtYoxuA4cLNtSZHA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-white/90 hover:text-white bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-md text-sm font-medium transition-all"
            >
              <Compass size={16} className="text-amber-300" />
              <span>Sanctuary Map & Directions</span>
            </a>
          </div>

          {/* Trust Highlights Strip */}
          <div className="pt-6 border-t border-white/15 flex flex-wrap items-center gap-6 sm:gap-8 text-xs text-stone-300 font-medium">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-amber-400" />
              <span>Private Family Sanctuary</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span>Organic Raised Beds & Groves</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span>Seasonal Harvest Celebrations</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gentle Scroll Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/60 pointer-events-none">
        <span className="text-[10px] tracking-widest uppercase font-semibold text-emerald-200/80">Scroll to Explore</span>
        <div className="w-[1px] h-8 bg-gradient-to-b from-emerald-300/80 to-transparent animate-pulse" />
      </div>
    </section>
  );
}
